import type { Education, Licence, ProfileView } from '@ts-internal/shared-lib';

import type { PublicProfileViewQuery } from 'src/graphql/Queries/publicProfileView.generated';

export const mapProfileViewForDisplay = (
  profile: PublicProfileViewQuery['publicProfileView'],
): ProfileView | null => {
  if (!profile) {
    return null;
  }

  const data = {
    slug: profile.slug,
    displayName: `${profile.firstName} ${profile.lastName}`,
    firstName: profile.firstName,
    lastName: profile.lastName,
    hasVerifiedCredentials: profile.hasVerifiedCredentials,
    homeLocation: profile.homeLocation?.label || '',
    profileGuid: profile.profileGuid,
  };

  if (profile.__typename === 'RestrictedPublicProfileView') {
    return {
      ...data,
      digitalIdentity: {
        isVerified: profile.digitalIdentity?.isVerified || false,
      },
      workHistories: profile.workHistories.map((workHistory) => ({
        companyName: workHistory.companyName,
        roleTitle: workHistory.roleTitle,
      })),
    };
  }

  const mappedEducation: Education[] | undefined = profile.education
    ? profile.education.map((educationItem) => {
        if (educationItem.__typename === 'VerifiedPublicProfileEducation') {
          return {
            name: educationItem.name,
            isVerified: true,
          };
        }

        return {
          name: educationItem.name,
          institute: educationItem.institute,
          isVerified: false,
          ...(educationItem.completion.label && {
            completionLabel: educationItem.completion.label,
          }),
          ...(educationItem.highlights && {
            highlights: educationItem.highlights,
          }),
        };
      })
    : undefined;

  const mappedLicences: Licence[] | undefined = profile.licences
    ? profile.licences.map((licence) => {
        if (licence.__typename === 'VerifiedPublicProfileLicence') {
          return {
            name: licence.name,
            isVerified: true,
          };
        }

        return {
          name: licence.name,
          issuingOrganisation: licence.issuingOrganisation ?? undefined,
          isVerified: false,
        };
      })
    : undefined;

  const result: ProfileView = {
    ...data,
    isOwnedByLoggedInUser: profile.isOwnedByLoggedInUser,
    digitalIdentity: {
      isVerified: profile.digitalIdentity?.isVerified || false,
      verifiedName: profile.digitalIdentity?.verifiedName || null,
    },
    personalSummary: profile.personalSummary || null,
    skills: profile.skills,
    languages: profile.languages,
    workHistories: profile.workHistories.map((workHistory) => ({
      companyName: workHistory.companyName,
      roleTitle: workHistory.roleTitle,
      duration: workHistory.duration?.label || undefined,
      achievements: workHistory.achievements || undefined,
    })),
  };

  if (mappedEducation) {
    result.education = mappedEducation;
  }

  if (mappedLicences) {
    result.licences = mappedLicences;
  }

  if (profile.rightsToWork) {
    result.rightsToWork = profile.rightsToWork;
  }

  return result;
};
